/**
 * Presentation rules for step 2 (f10.5) -- labels, pills and the transformation
 * list. Nothing here decides anything: cleaning, resolution, figures and every
 * capability come from the server. Shared wording helpers are step 1's.
 */

import type { BillableDaysReview } from '@/api/client';

export {
  hasStagedItems,
  isParsing,
  refusalMessage,
  severityPill,
} from '../usEngagements/usEngagementsView';

export const MONTH_LABELS: readonly string[] = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export const UNDECIDED = 'UNDECIDED';
export const MATCHED = 'MATCHED';
export const EXCLUDE = 'EXCLUDE';

const DECISION_PILLS: Readonly<Record<string, string>> = {
  MATCHED: 'pill-success',
  UNDECIDED: 'pill-warning',
  MAP: 'pill-info',
  EXCLUDE: 'pill-neutral',
};

export function decisionPill(code: string): string {
  return `badge ${DECISION_PILLS[code] ?? 'pill-neutral'}`;
}

/** How a matrix row reads: excluded columns struck through, undecided greyed. */
export function matrixRowClass(decision: string): string {
  if (decision === EXCLUDE) return 'bd-row-excluded';
  if (decision === UNDECIDED) return 'bd-row-undecided';
  return '';
}

/** "How it matched", spoken plainly. */
export function basisLabel(basis: string | null): string {
  if (basis === 'CODE') return 'by code';
  if (basis === 'NAME') return 'by name';
  if (basis === 'ALIAS') return 'by alias';
  return '';
}

export interface Transformation {
  ok: boolean;
  text: string;
}

function issueCount(review: BillableDaysReview, code: string): number {
  return review.issues.items.filter((issue) => issue.code === code).length;
}

/** The "Transformations applied" list, stated from the batch's own facts (design D4). */
export function transformations(review: BillableDaysReview): Transformation[] {
  const { batch, figures } = review;
  const totals = review.issues.items.find((issue) => issue.code === 'total_rows_dropped');
  const list: Transformation[] = [
    {
      ok: true,
      text: 'Columns bound by header name — Year, quarter_desc, month_desc and every “Billable Days AVG per FTE” country',
    },
    { ok: true, text: totals?.message ?? 'No total rows found — totals are never recomputed' },
    {
      ok: true,
      text: `Only ${figures.period_year} kept; values stored as exact decimals between 0 and 31`,
    },
    { ok: true, text: 'Countries resolved by code, then name, then alias — never fuzzily' },
  ];
  const gaps = issueCount(review, 'month_missing');
  if (gaps > 0) list.push({ ok: false, text: `${gaps} blank cells kept as gaps — listed under issues` });
  const unresolved = issueCount(review, 'country_unresolved');
  if (unresolved > 0) {
    list.push({ ok: false, text: `${unresolved} country columns matched no country — decide them below` });
  }
  if (batch.rejection_reason) list.push({ ok: false, text: batch.rejection_reason });
  return list;
}
